import { fetchWithTimeout } from '../utils';
import { parseDejavooConfig } from './Dejavoo';

const CART_SEND_TIMEOUT_MS = 15000;

// Sent in place of an empty Items array - covers both a fresh order with nothing added yet
// and an order that was just cancelled (its cart has been reset to empty).
const EMPTY_CART_PLACEHOLDER_ITEM = { Name: 'Waiting for New Order..', Price: 0, Quantity: 0 };

function round2(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function findProductForItem(item, products) {
  if (!Array.isArray(products) || item.product == null) return null;
  return products.find((p) => p && p.id != null && p.id.toString() === item.product.toString()) || null;
}

/** Groups an order item's flat modifier list back into named groups (Dejavoo "Modifiers"), each holding "Options". */
function buildItemModifierGroups(item, product) {
  const groups = [];
  (item.modifiers || [])
    .filter((mod) => mod && !mod.void_status)
    .forEach((mod) => {
      let group = groups.find((g) => g._groupId === mod.group);
      if (!group) {
        const productGroup = product ? (product.modifier_groups || []).find((g) => g.id === mod.group) : null;
        const name = (productGroup && productGroup.name) || mod.group_name || 'Modifiers';
        group = { _groupId: mod.group, Name: name, Options: [] };
        groups.push(group);
      }
      group.Options.push({
        Name: mod.name,
        Price: round2(mod.price),
        // Our modifiers aren't quantity-tracked yet; always 1 for now.
        Quantity: 1
      });
    });
  return groups.map(({ Name, Options }) => ({ Name, Options }));
}

function buildCartItems(order, products) {
  return (order.items || [])
    .filter((item) => item && !item.void_status)
    .map((item) => {
      const product = findProductForItem(item, products);
      const unitPrice = round2(item.price);
      const quantity = item.quantity || 1;
      const linePrice = item.grand_total != null ? round2(item.grand_total) : round2(unitPrice * quantity);

      return {
        Name: item.name,
        Price: linePrice,
        UnitPrice: unitPrice,
        Quantity: quantity,
        AdditionalInfo: item.notes || '',
        Modifiers: buildItemModifierGroups(item, product)
      };
    });
}

export function buildDejavooCartPayload(order, products, tpn, authkey, orderSource) {
  const items = buildCartItems(order, products);
  const isEmpty = items.length === 0;

  let amounts;
  if (isEmpty) {
    // Ignore whatever totals happen to be sitting on the order object (e.g. stale values
    // on a just-cancelled order) - an empty cart is always zeroed out.
    amounts = [
      { Name: 'Subtotal', Value: 0 },
      { Name: 'Discount', Value: 0 },
      { Name: 'Tax', Value: 0 },
      { Name: 'Total', Value: 0 }
    ];
  } else {
    // order_discount is display/allocation metadata only - it's already distributed into
    // items as wholeDiscount and folded into discount_total (see countSubtotal()'s comment
    // in orderMixin.js), so adding it again here would double the discount shown on the
    // terminal versus what the order-details panel shows (currentOrder.discount_total alone).
    const discountTotal = Math.abs(order.discount_total || 0);
    // order.subtotal is already net of discount; the terminal expects a pre-discount
    // Subtotal so that Subtotal + Discount (negative) + Tax reads correctly as a receipt.
    const grossSubtotal = round2(order.subtotal) + discountTotal;
    amounts = [
      { Name: 'Subtotal', Value: round2(grossSubtotal) },
      { Name: 'Discount', Value: round2(-discountTotal) },
      { Name: 'Tax', Value: round2(order.tax_total) },
      { Name: 'Total', Value: round2(order.grand_total) }
    ];
  }

  return {
    Cart: {
      Amounts: amounts,
      Items: isEmpty ? [EMPTY_CART_PLACEHOLDER_ITEM] : items
    },
    Tpn: tpn,
    Authkey: authkey,
    CustomFields: {
      OrderSource: orderSource
    }
  };
}

/**
 * Pushes the current cart to a Dejavoo terminal so it can render it on the customer-facing
 * screen, independent of (and additive to) any existing Customer Display broadcast.
 */
export async function sendDejavooCart(order, products, paymentType, orderSource) {
  const { baseUrl, tpn, authkey } = parseDejavooConfig(paymentType.url);
  const payload = buildDejavooCartPayload(order, products, tpn, authkey, orderSource);

  const response = await fetchWithTimeout(
    CART_SEND_TIMEOUT_MS,
    fetch(`${baseUrl}/Cart`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
  );

  if (!response.ok) {
    throw new Error(`Dejavoo cart send failed with status ${response.status}`);
  }

  return response;
}
